import { RENTAL_STRUCTURE_STATUS_ENUM } from '../common/enums';
import {
  selectAdminUpdateApplicationNumber,
  selectAdminUpdateToken,
  selectAdminUpdatedPermitData,
} from '../store/slices/adminUpdate/adminUpdateSelectors';
import { selectPermitApplicationData } from '../store/slices/apply/applySelectors';
import {
  selectCurrentIDToken,
  selectCurrentUser,
} from '../store/slices/auth/authSelectors';
import { selectConfigExpressApplyRentalStructureRequired } from '../store/slices/config/configSelectors';
import {
  selectRenewalPermitData,
  selectRenewApplicationNumber,
  selectSelectedBulkRenewalLicenses,
} from '../store/slices/renew/renewSelectors';
import {
  selectUpdatedPermitData,
  selectApplicationNumber,
} from '../store/slices/update/updateSelectors';
import { getAPIManagerInstance } from './apiManager';
import { DECKARD_API_URL, httpMethodFunctions } from './common';
import { sanitizePeople } from './utils';

const ERR_LICENSES = 'Something went wrong while fetching licenses.';

export const getAllLicensesForUserAsync = async (accessToken) => {
  const accumulatedLicenses = [];

  const fetchLicenses = async (contToken) => {
    try {
      const response = await getUsersLicensesAsync(accessToken, contToken);
      if (response.status === 200) {
        const { data } = response;
        accumulatedLicenses.push(...data.licenses);

        if (data.contToken) {
          // Recursively call the function if contToken exists
          return fetchLicenses(data.contToken);
        } else {
          // Return accumulated licenses
          return accumulatedLicenses;
        }
      } else {
        throw new Error(ERR_LICENSES);
      }
    } catch (error) {
      throw new Error(ERR_LICENSES);
    }
  };

  return fetchLicenses();
};

export const getUsersRenewableLicensesAsync = async (accessToken) => {
  return httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/renewableLicenses`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const createNewLicense = async (state) => {
  const {
    registrant,
    property,
    contacts,
    propertyDetails,
    listings,
    certifications,
  } = selectPermitApplicationData(state);

  const apn = property.propertyid;
  const accessToken = selectCurrentIDToken(state);

  const rentalStructureRequiredStatus =
    selectConfigExpressApplyRentalStructureRequired(state);

  let people = {
    ...sanitizePeople(registrant),
    ...sanitizePeople(contacts),
  };
  delete people.registrantType;

  const applicationConfig = {
    registrantType: registrant.registrantType,
    unitNumber: property.unitnumber,
    // only add rentalStructure property if it is not set to hidden in the config
    ...(rentalStructureRequiredStatus !==
      RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN && {
      rentalStructure: property?.rentalstructure ?? null,
    }),
    misc: {
      supplementalQuestions: { ...propertyDetails?.supplementalQuestions },
      mandatoryAcknowledgements: {
        ...certifications.mandatoryAcknowledgements,
      },
    },
    people: { ...people },
    signatory: certifications.signatory,
  };

  let parsedListings = listings?.map((l) => {
    return { url: l };
  });
  if (parsedListings?.length > 0) {
    applicationConfig.licenseListings = parsedListings;
  }

  const result = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/newLicense/${apn}`,
    applicationConfig,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return result?.data;
};

export const updateLicense = async (state) => {
  const applicationNumber = selectApplicationNumber(state);

  const { registrant, contacts, propertyDetails, certifications } =
    selectUpdatedPermitData(state);

  const accessToken = selectCurrentIDToken(state);

  let people = {
    ...sanitizePeople(registrant),
    ...sanitizePeople(contacts),
  };

  delete people.registrantType;

  const applicationConfig = {
    misc: {
      supplementalQuestions: { ...propertyDetails?.supplementalQuestions },
      mandatoryAcknowledgements: {
        ...certifications.mandatoryAcknowledgements,
      },
    },
    people: { ...people },
    signatory: certifications.signatory,
  };

  const result = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/updateLicense/${applicationNumber}`,
    applicationConfig,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return result?.data;
};

export const renewLicense = async (state) => {
  const applicationNumber = selectRenewApplicationNumber(state);

  const { registrant, contacts, propertyDetails, certifications } =
    selectRenewalPermitData(state);

  const accessToken = selectCurrentIDToken(state);

  let people = {
    ...sanitizePeople(registrant),
    ...sanitizePeople(contacts),
  };

  delete people.registrantType;

  const applicationConfig = {
    misc: {
      supplementalQuestions: { ...propertyDetails?.supplementalQuestions },
      mandatoryAcknowledgements: {
        ...certifications.mandatoryAcknowledgements,
      },
    },
    people: { ...people },
    signatory: certifications.signatory,
  };

  const result = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/renewLicense/${applicationNumber}`,
    applicationConfig,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return result?.data;
};

export const bulkRenewLicenses = async (state) => {
  // This needs refactor, these values should be passed as parameters
  const licenses = selectSelectedBulkRenewalLicenses(state);
  const licenseNumbers = licenses.map((l) => l.license);
  const { certifications } = selectRenewalPermitData(state);
  const { userId } = selectCurrentUser(state);

  const requestBody = {
    userId: userId,
    commonLicenseInfo: {
      misc: {
        mandatoryAcknowledgements: certifications.mandatoryAcknowledgements,
        signatoryTitle: certifications.signatory_title,
        signatory: certifications.signatory,
      },
    },
    renewalRequests: licenseNumbers.map((licenseNumber) => ({
      license: licenseNumber,
    })),
  };

  const accessToken = selectCurrentIDToken(state);

  const result = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/bulkRenewLicenses`,
    requestBody,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return {
    apiResponse: result?.data,
    licenseNumbers: licenseNumbers,
  };
};

export const getLicensesByAPN = async (apn, accessToken) => {
  try {
    const result = await httpMethodFunctions.get(
      `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/licensesByApn/${apn}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return result;
  } catch (err) {}
};

export const getUsersLicensesAsync = async (accessToken, contToken) => {
  const url = contToken
    ? `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/userLicenses?contToken=${contToken}`
    : `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/userLicenses`;

  return httpMethodFunctions.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

export const getLicenseAsync = async (state, applicationNumber) => {
  const accessToken = selectCurrentIDToken(state);
  return httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/license/${applicationNumber}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const getLookupLicenseAsync = async (state, number) => {
  const accessToken = selectCurrentIDToken(state);
  return httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/lookupLicense/${number}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const calculateLicenseFee = async (applicationNumber) => {
  const url = `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/calculateLicenseFee/${applicationNumber}`;
  const result = await httpMethodFunctions.get(url);
  return result?.data;
};

export const calculateLicenseRenwalFee = async (applicationNumber) => {
  const url = `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/calculateLicenseRenewalFee/${applicationNumber}`;
  const result = await httpMethodFunctions.get(url);
  return result?.data;
};

export const calculateBulkLicenseRenewalFee = async (licenseNumbers, state) => {
  const CHUNK_SIZE = 5;
  if (!Array.isArray(licenseNumbers)) return null;
  const accessToken = selectCurrentIDToken(state);

  let chunks = [];
  for (let i = 0; i < licenseNumbers.length; i += CHUNK_SIZE) {
    chunks.push(licenseNumbers.slice(i, i + CHUNK_SIZE));
  }
  const url = `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/calculateBulkLicenseRenewalFee`;

  let paymentFees = null;
  for (let chunk of chunks) {
    const result = await httpMethodFunctions.post(
      url,
      {
        renewalRequests: chunk.map((l) => ({ license: l })),
        numRenewalRequests: licenseNumbers.length,
        paymentId: paymentFees?.paymentId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    paymentFees = result?.data;
  }

  return paymentFees;
};

export const getReportingHistoryByApplicationNumber = async (
  state,
  applicationNumber
) => {
  const accessToken = selectCurrentIDToken(state);
  return httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/reportingHistoryByApplicationNumber/${applicationNumber}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const getPropertyContactsByApplicationNumber = async (
  state,
  applicationNumber
) => {
  const accessToken = selectCurrentIDToken(state);
  return httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/peopleByApplicationNumbers/${applicationNumber}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

export const getTaxPaymentPeriods = async (state) => {
  const accessToken = selectCurrentIDToken(state);
  return httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/totPaymentPeriods`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};

// #region Admin Update
export const getLicensesRentalscape = async (state) => {
  const accessToken = selectAdminUpdateToken(state);
  return httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/rentalscape/licenses`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      skipTokenRefresh: true,
    }
  );
};

export const getCallerIdentityRentalscape = async (state) => {
  const accessToken = selectAdminUpdateToken(state);
  return httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/rentalscape/callerIdentity`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      skipTokenRefresh: true,
    }
  );
};

export const getLicenseByApnRentalscape = async (state, apn, unitNumber) => {
  const accessToken = selectAdminUpdateToken(state);
  let url = `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/rentalscape/apn/${apn}`;
  // Add unitNumber to query string if valid
  if (!!unitNumber) {
    url += `?unitNumber=${encodeURIComponent(unitNumber)}`;
  }

  return httpMethodFunctions.get(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    skipTokenRefresh: true,
  });
};

export const getLicenseByApplicationNumberRentalscape = async (
  state,
  applicationNumber
) => {
  const accessToken = selectAdminUpdateToken(state);
  return httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/rentalscape/license/${applicationNumber}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      skipTokenRefresh: true,
    }
  );
};

export const updateLicenseRentalscape = async (state) => {
  const accessToken = selectAdminUpdateToken(state);
  const applicationNumber = selectAdminUpdateApplicationNumber(state);
  const rentalStructureRequiredStatus =
    selectConfigExpressApplyRentalStructureRequired(state);

  const { property, registrant, contacts, propertyDetails, updateAllPermits } =
    selectAdminUpdatedPermitData(state);

  let people = {
    ...sanitizePeople(registrant),
    ...sanitizePeople(contacts),
  };

  delete people.registrantType;

  const applicationConfig = {
    registrantType: registrant.registrantType,
    unitNumber: property.unitnumber,
    apn: property.propertyid,
    // only add rentalStructure property if it is not set to hidden in the config
    ...(rentalStructureRequiredStatus !==
      RENTAL_STRUCTURE_STATUS_ENUM.HIDDEN && {
      rentalStructure: property?.rentalstructure ?? null,
    }),
    misc: {
      supplementalQuestions: { ...propertyDetails?.supplementalQuestions },
    },
    updateAllPermits: updateAllPermits,
    people: { ...people },
  };

  const result = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/rentalscape/updateLicense/${applicationNumber}`,
    applicationConfig,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      skipTokenRefresh: true,
    }
  );
  return result?.data;
};

export const getPermitActionability = async (state, applicationNumber) => {
  const accessToken = selectCurrentIDToken(state);
  return httpMethodFunctions.get(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/permitActionability/${applicationNumber}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
};
